import React from 'react';
import Icon from '../../../components/AppIcon';

const TechnicianStats = ({ 
  stats = {
    ordersToday: 0,
    ordersCompleted: 0,
    hoursWorked: 0,
    earnings: 0,
    rating: 0,
    efficiency: 0
  }
}) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}min`;
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 90) return 'text-success';
    if (efficiency >= 70) return 'text-warning';
    return 'text-error';
  };

  const getRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={`full-${i}`} name="Star" size={14} className="text-warning fill-current" />
      );
    }
    
    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="StarHalf" size={14} className="text-warning fill-current" />
      );
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={14} className="text-muted-foreground" />
      );
    }
    
    return stars;
  };

  const statItems = [
    {
      label: 'Ordens Hoje',
      value: stats?.ordersToday,
      icon: 'ClipboardList',
      color: 'text-primary'
    },
    {
      label: 'Concluídas',
      value: stats?.ordersCompleted,
      icon: 'CheckCircle',
      color: 'text-success'
    },
    {
      label: 'Horas Trabalhadas',
      value: formatHours(stats?.hoursWorked),
      icon: 'Clock',
      color: 'text-accent'
    },
    {
      label: 'Ganhos do Dia',
      value: formatCurrency(stats?.earnings),
      icon: 'DollarSign',
      color: 'text-success'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-professional p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-card-foreground">
          Estatísticas do Dia
        </h3>
        <div className="text-xs text-muted-foreground">
          {new Date()?.toLocaleDateString('pt-BR')}
        </div>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {statItems?.map((item, index) => (
          <div key={index} className="bg-muted rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name={item?.icon} size={16} className={item?.color} />
              <span className="text-xs text-muted-foreground font-medium">
                {item?.label}
              </span>
            </div>
            <div className="text-lg font-semibold text-card-foreground">
              {item?.value}
            </div>
          </div>
        ))}
      </div>
      {/* Performance Metrics */}
      <div className="space-y-3">
        {/* Rating */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Avaliação</span>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              {getRatingStars(stats?.rating)}
            </div>
            <span className="text-sm font-medium text-card-foreground">
              {stats?.rating?.toFixed(1)}
            </span>
          </div>
        </div>

        {/* Efficiency */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Eficiência</span>
          <div className="flex items-center space-x-2">
            <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-300 ${
                  stats?.efficiency >= 90 ? 'bg-success' :
                  stats?.efficiency >= 70 ? 'bg-warning' : 'bg-error'
                }`}
                style={{ width: `${Math.min(stats?.efficiency, 100)}%` }}
              />
            </div>
            <span className={`text-sm font-medium ${getEfficiencyColor(stats?.efficiency)}`}>
              {stats?.efficiency}%
            </span>
          </div>
        </div>

        {/* Completion Rate */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Taxa de Conclusão</span>
          <span className="text-sm font-medium text-card-foreground">
            {stats?.ordersToday > 0 
              ? Math.round((stats?.ordersCompleted / stats?.ordersToday) * 100)
              : 0
            }%
          </span>
        </div>
      </div>
      {/* Weekly Comparison */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Comparado à semana passada</span>
          <div className="flex items-center space-x-1">
            <Icon name="TrendingUp" size={12} className="text-success" />
            <span className="text-success">+12%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicianStats;